'use client';

import { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import Cookies from 'js-cookie';
import { createContext, useContext, useEffect, useState } from 'react';
import client from '~/lib/client';
import { AuthService } from '~/shared/services/auth';
import { useSupabase } from '~/shared/utils/useSupabase';
import { formoLocalStorage } from '~/utils/storage';

const AuthContext = createContext<{
  session: Session | null | undefined;
  user: User | null | undefined;
  hasCompletedSurvey: boolean | null;
  setHasCompletedSurvey: (hasCompletedSurvey: boolean) => void;
  signOut: () => void;
}>({
  session: null,
  user: null,
  hasCompletedSurvey: null,
  setHasCompletedSurvey: () => {},
  signOut: () => {},
});

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<User | null>();
  const [session, setSession] = useState<Session | null>();
  const [loading, setLoading] = useState(true);

  /**
   * Check if user has completed survey
   * - Null: Not setup yet (need to fetch users/me to check user preferences)
   * - True: Completed
   * - False: Not completed
   *  */
  const surveyStatus = formoLocalStorage.getItem('hasCompletedSurvey');
  const [hasCompletedSurvey, setHasCompletedSurvey] = useState<boolean | null>(
    surveyStatus ? surveyStatus === 'true' : null,
  );

  const supabase = useSupabase();
  const Auth = new AuthService(Cookies);

  // Handle auth state change
  const handleAuthStateChange = async (
    _event: AuthChangeEvent,
    session: Session | null,
  ) => {
    // Set context values
    setSession(session);
    setUser(session?.user);
    setLoading(false);

    // Redirect to redirect_uri if user has completed survey
    const redirect_uri = sessionStorage.getItem('redirect_uri');
    if (
      redirect_uri &&
      formoLocalStorage.getItem('hasCompletedSurvey') === 'true'
    ) {
      sessionStorage.removeItem('redirect_uri');
      window.location.href = redirect_uri;
    }
  };

  // Check if user has completed survey
  useEffect(() => {
    // Check if hasCompletedSurvey is setup yet
    if (hasCompletedSurvey !== null) {
      return;
    }

    // Fetch user preferences from server
    const fetchUserPreferences = async () => {
      if (session) {
        try {
          const { data } = await client.get('/api/users/me');
          const hasPreferences = !!data?.preferences;
          formoLocalStorage.setItem('hasCompletedSurvey', hasPreferences);
          setHasCompletedSurvey(hasPreferences);
        } catch (error) {
          console.error('Error fetching user preferences', error);
        }
      }
    };
    fetchUserPreferences();
  }, [session]);

  // Set up auth state change listener
  useEffect(() => {
    // Listen for auth state changes when initializing
    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        handleAuthStateChange(event, session);
      },
    );

    // Clean up: unsubscribe from auth state changes
    return () => {
      listener?.subscription.unsubscribe();
    };
  }, [hasCompletedSurvey]);

  // Init auth context value
  const value = {
    session,
    user,
    hasCompletedSurvey,
    setHasCompletedSurvey: (hasCompletedSurvey: boolean) => {
      formoLocalStorage.setItem('hasCompletedSurvey', hasCompletedSurvey);
      setHasCompletedSurvey(hasCompletedSurvey);
    },
    signOut: async () => {
      await supabase.auth.signOut();
      Auth.removeToken();
      formoLocalStorage.removeItem('hasCompletedSurvey');
      setHasCompletedSurvey(false);
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
